import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { Card } from '../../components/common/Card';
import { Spinner } from '../../components/common/Spinner';
import { Users, DollarSign, Trophy, BarChart3, ArrowUpRight } from 'lucide-react';
import { adminService } from '../../services/adminService';

export const AdminDashboard: React.FC = () => {
  const { data: usersData, isLoading: usersLoading } = useQuery({
    queryKey: ['admin', 'users', { limit: 1 }],
    queryFn: () => adminService.getAllUsers({ limit: 1 }),
  });
  const { data: rafflesData, isLoading: rafflesLoading } = useQuery({
    queryKey: ['admin', 'raffles', { limit: 1 }],
    queryFn: () => adminService.getAllRaffles({ limit: 1 }),
  });
  const { data: pendingWithdrawals, isLoading: withdrawalsLoading } = useQuery({
    queryKey: ['admin', 'withdrawals', { status: 'PENDING', limit: 1 }],
    queryFn: () => adminService.getWithdrawals({ status: 'PENDING', limit: 1 }),
  });
  const { data: financeData, isLoading: financeLoading } = useQuery({
    queryKey: ['admin', 'finance', 'summary'],
    queryFn: () => adminService.getFinanceSummary(),
  });

  const isLoading = usersLoading || rafflesLoading || withdrawalsLoading || financeLoading;

  if (isLoading) {
    return <AdminLayout><div className="flex justify-center py-12"><Spinner size="lg" /></div></AdminLayout>;
  }

  const totalUsers = usersData?.data?.pagination?.total || 0;
  const totalRaffles = rafflesData?.data?.pagination?.total || 0;
  const pendingCount = pendingWithdrawals?.data?.pagination?.total || 0;
  const finance = financeData?.data;
  const fmt = (n: number) => n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const statCards = [
    { label: 'Total Users', value: totalUsers, icon: Users, color: 'indigo', link: '/admin/users' },
    { label: 'Total Raffles', value: totalRaffles, icon: Trophy, color: 'emerald', link: '/admin/raffles' },
    { label: 'Pending Withdrawals', value: pendingCount, icon: DollarSign, color: 'amber', link: '/admin/withdrawals' },
    { label: 'Tickets Sold', value: (finance?.operations.totalTicketsSold || 0).toLocaleString(), icon: BarChart3, color: 'violet', link: '/admin/finance' },
  ];

  const colorMap: Record<string, { iconBg: string; iconText: string; valueBg: string }> = {
    indigo: { iconBg: 'bg-indigo-100 dark:bg-indigo-900/50', iconText: 'text-indigo-600 dark:text-indigo-400', valueBg: 'bg-indigo-50 dark:bg-indigo-900/30' },
    emerald: { iconBg: 'bg-emerald-100 dark:bg-emerald-900/50', iconText: 'text-emerald-600 dark:text-emerald-400', valueBg: 'bg-emerald-50 dark:bg-emerald-900/30' },
    amber: { iconBg: 'bg-amber-100 dark:bg-amber-900/50', iconText: 'text-amber-600 dark:text-amber-400', valueBg: 'bg-amber-50 dark:bg-amber-900/30' },
    violet: { iconBg: 'bg-violet-100 dark:bg-violet-900/50', iconText: 'text-violet-600 dark:text-violet-400', valueBg: 'bg-violet-50 dark:bg-violet-900/30' },
  };

  return (
    <AdminLayout>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Dashboard</h1>
        <p className="text-gray-500 dark:text-gray-400 mt-1">Overview of your platform</p>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
        {statCards.map((card) => {
          const c = colorMap[card.color];
          const Icon = card.icon;
          return (
            <Link key={card.label} to={card.link}>
              <Card className="group hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 relative overflow-hidden">
                <div className={`absolute top-0 right-0 w-24 h-24 ${c.valueBg} rounded-full -mr-8 -mt-8 opacity-60`} />
                <div className="relative flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">{card.label}</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{card.value}</p>
                  </div>
                  <div className={`${c.iconBg} p-3 rounded-xl`}>
                    <Icon className={`h-6 w-6 ${c.iconText}`} />
                  </div>
                </div>
              </Card>
            </Link>
          );
        })}
      </div>

      {/* Financial Summary */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Financial Overview</h2>
        <Link to="/admin/finance" className="flex items-center gap-1 text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 font-medium group">
          View details <ArrowUpRight className="h-3 w-3 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
        </Link>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-8">
        <Card className="border-l-4 border-l-emerald-500">
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Gross Revenue</p>
          <p className="text-2xl font-bold text-emerald-700 dark:text-emerald-400">{fmt(finance?.revenue.grossRevenue || 0)}</p>
          <div className="flex items-center gap-4 mt-3 text-xs text-gray-400 dark:text-gray-500">
            <span>Platform fees: {fmt(finance?.revenue.platformFees || 0)}</span>
            <span>Withdrawal fees: {fmt(finance?.revenue.withdrawalFees || 0)}</span>
          </div>
        </Card>
        <Card className="border-l-4 border-l-red-400">
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Expenses</p>
          <p className="text-2xl font-bold text-red-600 dark:text-red-400">{fmt(finance?.revenue.affiliateCommissions || 0)}</p>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-3">{finance?.revenue.affiliateCommissionCount || 0} affiliate commission payouts</p>
        </Card>
        <Card className="border-l-4 border-l-indigo-500">
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Net Revenue</p>
          <p className={`text-2xl font-bold ${(finance?.revenue.netRevenue || 0) >= 0 ? 'text-indigo-700 dark:text-indigo-400' : 'text-red-600 dark:text-red-400'}`}>
            {fmt(finance?.revenue.netRevenue || 0)}
          </p>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-3">
            Total draw sales: {fmt(finance?.operations.totalDrawSales || 0)}
          </p>
        </Card>
      </div>

      {/* Operations Stats */}
      <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Platform Operations</h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        <Card>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Total Ticket Sales</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{fmt(finance?.operations.totalTicketSales || 0)}</p>
        </Card>
        <Card>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Draws Completed</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{finance?.operations.totalDraws || 0}</p>
        </Card>
        <Card>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Organizer Payouts</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{fmt(finance?.operations.totalOrganizerPayouts || 0)}</p>
        </Card>
      </div>
    </AdminLayout>
  );
};
